import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { squarePaymentService, SUBSCRIPTION_PLANS } from '../services/squarePayment';

// Payment method types
export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account' | 'digital_wallet';
  last4?: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
  createdAt: Date;
}

// Subscription details
export interface SubscriptionDetails {
  id: string;
  planId: string;
  planName: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  nextBillingDate?: Date;
  amount: number;
  currency: string;
  paymentMethodId: string;
  createdAt: Date;
  canceledAt?: Date;
  cancelAtPeriodEnd: boolean;
}

// Transaction history
export interface Transaction {
  id: string;
  type: 'payment' | 'refund' | 'failed_payment';
  amount: number;
  currency: string;
  status: 'completed' | 'pending' | 'failed';
  description: string;
  paymentMethodId?: string;
  subscriptionId?: string;
  createdAt: Date;
  receiptUrl?: string;
}

// Payment stats
export interface PaymentStats {
  totalSpent: number;
  subscriptionsCount: number;
  activeSubscriptions: number;
  nextBillingAmount: number;
  nextBillingDate?: Date;
}

interface PaymentContextType {
  // Payment Methods
  paymentMethods: PaymentMethod[];
  isLoadingPaymentMethods: boolean;
  addPaymentMethod: (token: string, isDefault?: boolean) => Promise<PaymentMethod>;
  removePaymentMethod: (paymentMethodId: string) => Promise<void>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  
  // Subscriptions
  subscriptions: SubscriptionDetails[];
  isLoadingSubscriptions: boolean;
  createSubscription: (planId: string, paymentMethodId: string) => Promise<SubscriptionDetails>;
  updateSubscription: (subscriptionId: string, newPlanId: string) => Promise<SubscriptionDetails>;
  cancelSubscription: (subscriptionId: string, immediate?: boolean) => Promise<void>;
  reactivateSubscription: (subscriptionId: string) => Promise<SubscriptionDetails>;
  
  // Transactions
  transactions: Transaction[];
  isLoadingTransactions: boolean;
  loadTransactions: (limit?: number) => Promise<void>;
  
  // Stats
  paymentStats: PaymentStats;
  
  // Utility
  refreshData: () => Promise<void>;
  isLoading: boolean;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within PaymentProvider');
  }
  return context;
};

interface PaymentProviderProps {
  children: ReactNode;
  customerId?: string;
}

export const PaymentProvider: React.FC<PaymentProviderProps> = ({ 
  children, 
  customerId = 'demo-customer' 
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionDetails[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [paymentStats, setPaymentStats] = useState<PaymentStats>({
    totalSpent: 0,
    subscriptionsCount: 0,
    activeSubscriptions: 0,
    nextBillingAmount: 0
  });

  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  const isLoading = isLoadingPaymentMethods || isLoadingSubscriptions || isLoadingTransactions;

  // Load payment methods
  const loadPaymentMethods = useCallback(async () => {
    setIsLoadingPaymentMethods(true);
    try {
      // In real app, fetch from backend API
      // const paymentMethods = await paymentService.getPaymentMethods(customerId);
      // setPaymentMethods(paymentMethods);
      
      // For now, set empty array until backend integration
      setPaymentMethods([]);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  }, []);

  // Load subscriptions
  const loadSubscriptions = useCallback(async () => {
    setIsLoadingSubscriptions(true);
    try {
      // In real app, fetch from backend API
      // const subscriptions = await squarePaymentService.getSubscriptions(customerId);
      // setSubscriptions(subscriptions);
      
      // For now, set empty array until backend integration
      setSubscriptions([]);
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
      setSubscriptions([]);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  }, []);

  // Load transactions
  const loadTransactions = useCallback(async (limit = 50) => {
    setIsLoadingTransactions(true);
    try {
      // In real app, fetch from backend API
      // const transactions = await squarePaymentService.getTransactions(customerId, limit);
      // setTransactions(transactions);
      
      // For now, set empty array until backend integration
      setTransactions([]);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      setTransactions([]);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, []);

  // Calculate payment stats
  const calculateStats = useCallback(() => {
    const totalSpent = transactions
      .filter(t => t.type === 'payment' && t.status === 'completed')
      .reduce((sum, t) => sum + t.amount, 0);

    const activeSubscriptions = subscriptions.filter(s => s.status === 'active').length;
    
    const nextBillingDate = subscriptions
      .filter(s => s.status === 'active' && s.nextBillingDate)
      .sort((a, b) => (a.nextBillingDate!.getTime() - b.nextBillingDate!.getTime()))[0]?.nextBillingDate;

    const nextBillingAmount = subscriptions
      .filter(s => s.status === 'active')
      .reduce((sum, s) => sum + s.amount, 0);

    setPaymentStats({
      totalSpent,
      subscriptionsCount: subscriptions.length,
      activeSubscriptions,
      nextBillingAmount,
      nextBillingDate
    });
  }, [transactions, subscriptions]);

  // Add payment method
  const addPaymentMethod = useCallback(async (token: string, isDefault = false): Promise<PaymentMethod> => {
    try {
      // In real app, send token to backend to create payment method
      const newPaymentMethod: PaymentMethod = {
        id: `pm_${Date.now()}`,
        type: 'card',
        last4: '4242', // Would come from Square response
        brand: 'Visa', // Would come from Square response
        expiryMonth: 12, // Would come from Square response
        expiryYear: 2025, // Would come from Square response
        isDefault: isDefault || paymentMethods.length === 0,
        createdAt: new Date()
      };

      // If this is set as default, update others
      if (newPaymentMethod.isDefault) {
        setPaymentMethods(prev => prev.map(pm => ({ ...pm, isDefault: false })));
      }

      setPaymentMethods(prev => [...prev, newPaymentMethod]);
      return newPaymentMethod;
    } catch (error) {
      console.error('Failed to add payment method:', error);
      throw error;
    }
  }, [paymentMethods.length]);

  // Remove payment method
  const removePaymentMethod = useCallback(async (paymentMethodId: string): Promise<void> => {
    try {
      // In real app, call backend API
      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
    } catch (error) {
      console.error('Failed to remove payment method:', error);
      throw error;
    }
  }, []);

  // Set default payment method
  const setDefaultPaymentMethod = useCallback(async (paymentMethodId: string): Promise<void> => {
    try {
      // In real app, call backend API
      setPaymentMethods(prev => prev.map(pm => ({
        ...pm,
        isDefault: pm.id === paymentMethodId
      })));
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      throw error;
    }
  }, []);

  // Create subscription
  const createSubscription = useCallback(async (planId: string, paymentMethodId: string): Promise<SubscriptionDetails> => {
    try {
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Invalid plan ID');
      }

      // In real app, call backend API
      const newSubscription: SubscriptionDetails = {
        id: `sub_${Date.now()}`,
        planId,
        planName: plan.name === 'premium' ? 'Premium' : 'Platinum',
        status: 'active',
        currentPeriodStart: new Date(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        amount: plan.price,
        currency: plan.currency,
        paymentMethodId,
        createdAt: new Date(),
        cancelAtPeriodEnd: false
      };

      setSubscriptions(prev => [...prev, newSubscription]);
      return newSubscription;
    } catch (error) {
      console.error('Failed to create subscription:', error);
      throw error;
    }
  }, []);

  // Update subscription
  const updateSubscription = useCallback(async (subscriptionId: string, newPlanId: string): Promise<SubscriptionDetails> => {
    try {
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === newPlanId);
      if (!plan) {
        throw new Error('Invalid plan ID');
      }

      // In real app, call backend API
      setSubscriptions(prev => prev.map(sub => 
        sub.id === subscriptionId 
          ? { 
              ...sub, 
              planId: newPlanId, 
              planName: plan.name === 'premium' ? 'Premium' : 'Platinum',
              amount: plan.price 
            }
          : sub
      ));

      const updatedSub = subscriptions.find(s => s.id === subscriptionId);
      if (!updatedSub) {
        throw new Error('Subscription not found');
      }

      return { ...updatedSub, planId: newPlanId, amount: plan.price };
    } catch (error) {
      console.error('Failed to update subscription:', error);
      throw error;
    }
  }, [subscriptions]);

  // Cancel subscription
  const cancelSubscription = useCallback(async (subscriptionId: string, immediate = false): Promise<void> => {
    try {
      // In real app, call backend API
      setSubscriptions(prev => prev.map(sub => 
        sub.id === subscriptionId 
          ? { 
              ...sub, 
              status: immediate ? 'canceled' : sub.status,
              cancelAtPeriodEnd: !immediate,
              canceledAt: immediate ? new Date() : undefined
            }
          : sub
      ));
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw error;
    }
  }, []);

  // Reactivate subscription
  const reactivateSubscription = useCallback(async (subscriptionId: string): Promise<SubscriptionDetails> => {
    try {
      // In real app, call backend API
      setSubscriptions(prev => prev.map(sub => 
        sub.id === subscriptionId 
          ? { 
              ...sub, 
              status: 'active',
              cancelAtPeriodEnd: false,
              canceledAt: undefined
            }
          : sub
      ));

      const reactivatedSub = subscriptions.find(s => s.id === subscriptionId);
      if (!reactivatedSub) {
        throw new Error('Subscription not found');
      }

      return { ...reactivatedSub, status: 'active' };
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      throw error;
    }
  }, [subscriptions]);

  // Refresh all data
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadPaymentMethods(),
      loadSubscriptions(),
      loadTransactions()
    ]);
  }, [loadPaymentMethods, loadSubscriptions, loadTransactions]);

  // Initial data load
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Recalculate stats when data changes
  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  const contextValue: PaymentContextType = {
    // Payment Methods
    paymentMethods,
    isLoadingPaymentMethods,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    
    // Subscriptions
    subscriptions,
    isLoadingSubscriptions,
    createSubscription,
    updateSubscription,
    cancelSubscription,
    reactivateSubscription,
    
    // Transactions
    transactions,
    isLoadingTransactions,
    loadTransactions,
    
    // Stats
    paymentStats,
    
    // Utility
    refreshData,
    isLoading
  };

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext;